import { useState } from 'react';
import type { Event, EventType } from '../../../types/event';
import type { Setlist } from '../../../types/setlist';

interface Props {
  teamId: string;
  eventTypes: EventType[];
  setlists: Setlist[];
  event?: Event | null;
  onSave: (payload: {
    name: string;
    date: string;
    event_type_id?: string | null;
    start_time?: string | null;
    end_time?: string | null;
    setlist_id?: string | null;
    notes?: string;
  }) => Promise<void>;
  onClose: () => void;
}

export default function EventModal({ eventTypes, setlists, event, onSave, onClose }: Props) {
  const [name, setName] = useState(event?.name ?? '');
  const [date, setDate] = useState(event?.date ?? '');
  const [eventTypeId, setEventTypeId] = useState(event?.event_type_id ?? '');
  const [startTime, setStartTime] = useState(event?.start_time ?? '');
  const [endTime, setEndTime] = useState(event?.end_time ?? '');
  const [setlistId, setSetlistId] = useState(event?.setlist_id ?? '');
  const [notes, setNotes] = useState(event?.notes ?? '');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim() || !date) return;
    setIsSubmitting(true);
    setError(null);
    try {
      await onSave({
        name: name.trim(),
        date,
        event_type_id: eventTypeId || null,
        start_time: startTime || null,
        end_time: endTime || null,
        setlist_id: setlistId || null,
        notes,
      });
      onClose();
    } catch {
      setError('Falha ao salvar evento');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
      <div className="bg-[#111118] border border-white/10 rounded-2xl p-6 w-full max-w-md">
        <h2 className="text-lg font-semibold text-white mb-4">
          {event ? 'Editar Evento' : 'Novo Evento'}
        </h2>
        {error && (
          <div className="mb-4 p-3 bg-red-500/10 border border-red-500/20 text-red-400 rounded-xl text-sm">
            {error}
          </div>
        )}
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-xs text-white/40 mb-1">Nome</label>
            <input
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="w-full bg-white/5 border border-white/10 rounded-xl px-3 py-2 text-white text-sm outline-none focus:border-[#C8A96E]/50"
              placeholder="Nome do evento"
              required
            />
          </div>
          <div>
            <label className="block text-xs text-white/40 mb-1">Data</label>
            <input
              type="date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              className="w-full bg-white/5 border border-white/10 rounded-xl px-3 py-2 text-white text-sm outline-none focus:border-[#C8A96E]/50"
              required
            />
          </div>
          <div>
            <label className="block text-xs text-white/40 mb-1">Tipo de Evento</label>
            <select
              value={eventTypeId}
              onChange={(e) => setEventTypeId(e.target.value)}
              className="w-full bg-white/5 border border-white/10 rounded-xl px-3 py-2 text-white text-sm outline-none focus:border-[#C8A96E]/50"
            >
              <option value="">Sem tipo</option>
              {eventTypes.map((et) => (
                <option key={et.id} value={et.id}>
                  {et.name}
                </option>
              ))}
            </select>
          </div>
          <div className="flex gap-3">
            <div className="flex-1">
              <label className="block text-xs text-white/40 mb-1">Início</label>
              <input
                type="time"
                value={startTime}
                onChange={(e) => setStartTime(e.target.value)}
                className="w-full bg-white/5 border border-white/10 rounded-xl px-3 py-2 text-white text-sm outline-none focus:border-[#C8A96E]/50"
              />
            </div>
            <div className="flex-1">
              <label className="block text-xs text-white/40 mb-1">Fim</label>
              <input
                type="time"
                value={endTime}
                onChange={(e) => setEndTime(e.target.value)}
                className="w-full bg-white/5 border border-white/10 rounded-xl px-3 py-2 text-white text-sm outline-none focus:border-[#C8A96E]/50"
              />
            </div>
          </div>
          <div>
            <label className="block text-xs text-white/40 mb-1">Setlist vinculado</label>
            <select
              value={setlistId}
              onChange={(e) => setSetlistId(e.target.value)}
              className="w-full bg-white/5 border border-white/10 rounded-xl px-3 py-2 text-white text-sm outline-none focus:border-[#C8A96E]/50"
            >
              <option value="">Nenhum setlist</option>
              {setlists.map((s) => (
                <option key={s.id} value={s.id}>{s.name}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs text-white/40 mb-1">Notas</label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={2}
              className="w-full bg-white/5 border border-white/10 rounded-xl px-3 py-2 text-white text-sm outline-none focus:border-[#C8A96E]/50 resize-none"
            />
          </div>
          <div className="flex gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 py-2.5 border border-white/10 text-white/60 hover:text-white rounded-xl text-sm transition-colors"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className="flex-1 py-2.5 bg-[#C8A96E] text-[#0A0A0F] font-semibold rounded-xl text-sm hover:bg-[#D4B87A] disabled:opacity-50 transition-colors"
            >
              {isSubmitting ? 'Salvando...' : 'Salvar'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
